import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isFirefox } from 'react-device-detect';
import { useTranslation } from 'react-i18next';

import { defaultLivePostCover, defaultLivePreCover } from '../../util/config';
import { isAllowAutoPlay, joinClassNames, STORY_CONFIG_ID, toHHMMSS } from '../../util/util';
import { getHighQCoverFromRenditions, getHighQThumbnail, getHighQThumbnailFromResource } from '../../util/media-data-util';
import { NumberHelper } from '../../util/number-helper';
import { DateTimeHelper } from '../../util/date-time-helper';
import * as QPlayerSVGElement from '../QPlayerSVGElement';
import { useConfiguration } from '../../contexts/ConfigurationContext';
import { usePlayer } from '../../contexts/PlayerContext';

import './Poster.scss';

const DEFAULT_COLOR = '#126cfc';
const DEFAULT_PLAY_BUTTON_WIDTH = 120;
const DEFAULT_PLAY_BUTTON_X = 50;
const DEFAULT_PLAY_BUTTON_Y = 50;

interface PosterProps {
	isEditorPlayer: any;
	isMobile: boolean;
	playerConfigs: any;
}

const Poster = ({ isEditorPlayer, isMobile, playerConfigs }: PosterProps) => {
	const { t } = useTranslation();

	const { mediasData, mediaType, isMediaLive, showCountdownModule, currentMediaId, minimizedPlayer, configParams, showRegistrationForm, playlistId } = useConfiguration();
	const { isPosterRemoved, isPlaying, playerReady, isLiveStarted, isLiveStopped, currentPosition, duration, setIsPlaying, setRemovePoster } = usePlayer();

	const [isNoThumbnail, setNoThumbnail] = useState(false);
	const [isPlayButtonHover, setPlayButtonHover] = useState(false);
	const [isPlayButtonFocus, setPlayButtonFocus] = useState(false);
	const [thumbnailDimension, setThumbnailDimension] = useState({ width: 0, height: 0 });

	const thumbnailRef = useRef<HTMLDivElement>(null);

	const isStoryPlaylist = playlistId && typeof configParams?.configId === "string" && configParams.configId.toLowerCase().includes(STORY_CONFIG_ID.toLowerCase());
	const currentPlayerId = `player-${isStoryPlaylist ? currentMediaId : (playlistId || currentMediaId)}-${configParams.containerKey}`;
	const currentMedia = useMemo(() => mediasData.find(({ id, originalId }: any) => id === currentMediaId || originalId === currentMediaId), [mediasData, currentMediaId]);
	const isAllowToAutoPlay = useMemo(() => {
		return isEditorPlayer || isAllowAutoPlay({ configParams, settings: playerConfigs });
	}, [isEditorPlayer, configParams, playerConfigs]);

	const playBtnConfig = useMemo(() => {
		const color = playerConfigs?.playerStyling?.primaryColor ?? DEFAULT_COLOR;
		const icon = (playerConfigs?.playCover?.customPlayButtonIcon ? playerConfigs?.playCover?.customPlayButtonUrl : '') ?? '';
		const btnWidth = playerConfigs?.playCover?.playButtonSizeWidth ?? DEFAULT_PLAY_BUTTON_WIDTH;
		const btnHeight = icon ? null : btnWidth * 0.6;

		const xByPercentage = playerConfigs?.playCover?.playButtonPositionX ?? DEFAULT_PLAY_BUTTON_X;
		const yByPercentage = playerConfigs?.playCover?.playButtonPositionY ?? DEFAULT_PLAY_BUTTON_Y;

		const isShowVideoLength = icon || mediaType === 'scenario' || isMediaLive ? false : playerConfigs?.playCover?.showVideoLength ?? false;

		return { color, icon, width: btnWidth, height: btnHeight, x: xByPercentage, y: yByPercentage, isShowVideoLength };
	}, [playerConfigs, mediaType, isMediaLive, thumbnailDimension]);

	const { thumbnailUrl, preCoverUrl, postCoverUrl } = useMemo(() => {
		let posterMedia = currentMedia;

		const thumbnailMediaId = configParams?.thumbnailMediaId;
		if (thumbnailMediaId) {
			posterMedia = mediasData.find(({ id, originalId }: any) => id === thumbnailMediaId || originalId === thumbnailMediaId) ?? currentMedia;
		}

		if (!posterMedia || !posterMedia?.mediaDetails) {
			setNoThumbnail(false)
			return {};
		}

		if ((posterMedia.playlistAsset?.resources ?? []).length > 0) {
			const { resources } = posterMedia.playlistAsset;
			const thumbnailUrl = getHighQThumbnailFromResource(resources)
			setNoThumbnail(!thumbnailUrl)
			return { thumbnailUrl };
		}

		const { posters, images } = posterMedia;
		const thumbnailUrl = getHighQThumbnail({ posters, images });
		setNoThumbnail(!thumbnailUrl)
		return {
			thumbnailUrl: thumbnailUrl,
			preCoverUrl: getHighQCoverFromRenditions(images, "precover") || defaultLivePreCover,
			postCoverUrl: getHighQCoverFromRenditions(images, "postcover") || defaultLivePostCover,
		};
	}, [configParams?.thumbnailMediaId, currentMedia, mediasData]);

	const handlePlay = () => {
		const containerElem = configParams.container || document;
		const qplayerAppElem = containerElem.querySelector('div.qplayer-app') as any;
		setTimeout(() => {
			qplayerAppElem?.focus();
			setRemovePoster();
		});

		currentPlayerId && (window as any).GoBrain.widgets(currentPlayerId)?.play?.();
		if (isFirefox) {
			// Work around for need to click twice to play on Firefox
			setTimeout(() => {
				currentPlayerId && (window as any).GoBrain.widgets(currentPlayerId)?.play?.();
				setRemovePoster();
			}, 100);
		}
	};

	const showPostCover = !isLiveStopped || typeof isLiveStopped === "boolean" ? isLiveStopped : (isPlaying === undefined || (isPlaying && mediaType === "scenario")) && Math.ceil(NumberHelper.precision3(typeof currentPosition === "number" ? currentPosition : 0)) === Math.ceil(NumberHelper.precision3(isLiveStopped));

	useEffect(() => {
		if (isMediaLive && showPostCover) {
			setIsPlaying(undefined);
		}
	}, [isMediaLive, showPostCover]);

	useEffect(() => {
		thumbnailRef.current &&
			new ResizeObserver((changes) => {
				changes.forEach((change) => {
					const { contentRect } = change;

					if (contentRect?.width && contentRect?.height) {
						setThumbnailDimension({ width: contentRect?.width, height: contentRect?.height });
					}
				});
			}).observe(thumbnailRef.current);
	}, []);

	const renderPlayButton = () =>
		!playerReady ? null : playBtnConfig.icon ? (
			<div
				className="play-button"
				tabIndex={0}
				role="button"
				aria-label={`${t('PLAY_WITH_DURATION_ARIA_LABEL')} ${DateTimeHelper.formatHumanizeDuration(toHHMMSS(duration || currentMedia?.duration), t)}`}
				style={{
					top: `${playBtnConfig.y}%`,
					left: `${playBtnConfig.x}%`,
					width: `${playBtnConfig.width}px`,
					height: playBtnConfig.height ?? 'auto',
				}}
				onMouseEnter={isMobile ? undefined : () => setPlayButtonHover(true)}
				onMouseLeave={isMobile ? undefined : () => setPlayButtonHover(false)}
				onFocus={isMobile ? undefined : () => setPlayButtonFocus(true)}
				onBlur={isMobile ? undefined : () => { setPlayButtonFocus(false); setPlayButtonHover(false); }}
				onClick={handlePlay}
				onKeyDown={(e) => {
					if (e.key === 'Escape') { setPlayButtonHover(false); setPlayButtonFocus(false); return; }
					if (e.key === 'Enter' || e.key === ' ') {
						handlePlay();
					}
				}}
			>
				<div className="play-icon full-width" aria-hidden={true}>
					<img src={playBtnConfig.icon} alt="play-icon" />
					<div
						className="custom-icon-overlay"
						style={{
							backgroundColor: isPlayButtonHover || isPlayButtonFocus ? playBtnConfig.color : 'transparent',
							WebkitMaskImage: `url(${playBtnConfig.icon})`,
							maskImage: `url(${playBtnConfig.icon})`,
							maskSize: 'contain',
							maskRepeat: 'no-repeat',
							maskPosition: 'center',
						}}
					/>
				</div>
				<div
					aria-hidden="true"
					className={joinClassNames([
						'controlbar__control-tooltip',
						'controlbar__control-tooltip--light',
						isPlayButtonHover || isPlayButtonFocus ? 'controlbar__control-tooltip--visible' : '',
					])}
				>
					<div className="controlbar__control-tooltip-text">{t('PLAY')}</div>
				</div>
			</div>
		) : (
			<div
				className={`play-button${isPlayButtonHover ? ' hovered' : ''}`}
				tabIndex={0}
				role="button"
				aria-label={`${t('PLAY_WITH_DURATION_ARIA_LABEL')} ${DateTimeHelper.formatHumanizeDuration(toHHMMSS(duration || currentMedia?.duration), t)}`}
				style={{
					backgroundColor: isPlayButtonHover || isPlayButtonFocus ? playBtnConfig.color : '#000000cc',
					top: `${playBtnConfig.y}%`,
					left: `${playBtnConfig.x}%`,
					width: `${playBtnConfig.width}px`,
					height: `${playBtnConfig.height}px`,
					color: '#ffffff'
				}}
				onMouseEnter={isMobile ? undefined : () => setPlayButtonHover(true)}
				onMouseLeave={isMobile ? undefined : () => setPlayButtonHover(false)}
				onFocus={isMobile ? undefined : () => setPlayButtonFocus(true)}
				onBlur={isMobile ? undefined : () => { setPlayButtonFocus(false); setPlayButtonHover(false); }}
				onClick={handlePlay}
				onKeyDown={(e) => {
					if (e.key === 'Escape') { setPlayButtonHover(false); setPlayButtonFocus(false); return; }
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						handlePlay();
					}
				}}
			>
				<div className="play-icon" aria-hidden={true}>
					<QPlayerSVGElement.Play />
				</div>

				{playBtnConfig.isShowVideoLength && !!(duration || currentMedia?.duration) && (
					<div aria-hidden={true} className={`video-duration`}>
						{toHHMMSS(duration || currentMedia?.duration)}
					</div>
				)}
				<div
					aria-hidden="true"
					className={joinClassNames([
						'controlbar__control-tooltip',
						'controlbar__control-tooltip--light',
						isPlayButtonHover || isPlayButtonFocus ? 'controlbar__control-tooltip--visible' : '',
					])}
				>
					<div className="controlbar__control-tooltip-text">{t('PLAY')}</div>
				</div>
			</div>
		);

	if (showCountdownModule || showRegistrationForm || isEditorPlayer || (configParams?.inline !== "on" && configParams?.useLoader === "true" && isAllowToAutoPlay)) {
		return null;
	}

	// Live stop, show post-cover if there's one
	if (isMediaLive && showPostCover && postCoverUrl) {
		return (
			<div
				className={`qplayer-poster`}
				ref={thumbnailRef}
				style={postCoverUrl ? { backgroundImage: `url('${postCoverUrl}')`, } : undefined}
			/>
		);
	}

	// Live hasn't started yet, show pre-cover if there's one
	if (isMediaLive && !isLiveStarted && preCoverUrl && !isLiveStopped) {
		return (
			<div
				className={`qplayer-poster`}
				ref={thumbnailRef}
				style={preCoverUrl ? { backgroundImage: `url('${preCoverUrl}')`, } : undefined}
			/>
		);
	}

	if (isAllowToAutoPlay) {
		return null;
	}

	// Live started, show thumbnail if it isn't autoplay
	return (
		(!!isLiveStarted || !isMediaLive) &&
			!isLiveStopped &&
			!isPosterRemoved &&
			!isPlaying ? (
			<div
				className={joinClassNames(`qplayer-poster`, isNoThumbnail ? 'qplayer-poster--no-thumbnail' : '', minimizedPlayer ? 'qplayer-poster--minimized' : '')}
				ref={thumbnailRef}
				style={thumbnailUrl ? { backgroundImage: `url('${thumbnailUrl}')`, } : undefined}
			>
				{configParams?.hidePlayButton !== "true" && !minimizedPlayer && renderPlayButton()}
			</div>
		) : null
	);
};

export default Poster;
